import { Box, Button, Flex, Paper, Text, TextInput, SimpleGrid, NumberInput } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import React, { useState, useEffect } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useFund } from '../../hooks/useFund';
import { useUpdateFund } from '../../hooks/useUpdateFund';
import { FundSearch } from './FundSearch';
import FundDocuments from './FundDocuments';
import { fundSchema, EditableFund } from '../schemas/fundSchema';
import CompanyPageLoadingSection from 'src/company/CompanyPageLoadingSection';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useParams, useNavigate } from 'react-router';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { AfternoonDuplicateFieldApiError } from 'src/error/ApiError';
import { formatCurrency } from 'src/common/currency';
import { formatPercentage } from 'src/common/percentage';
import { Button as DesignButton } from 'src/design-system/Button';

export default function FundDetailPage() {
  const { fundId } = useParams<{ fundId: string }>();
  const navigate = useNavigate();
  const { fund, isLoading } = useFund(fundId);
  const { updateFund } = useUpdateFund();
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm<EditableFund>({
    initialValues: {
      name: '',
      pricePerUnit: 0,
      code: '',
      amcPercentage: 0,
    },
    validate: zodResolver(fundSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (fund) {
      form.setValues(fund);
    }
  }, [fund]);

  const handleSave = async () => {
    try {
      if (fund && form.isValid()) {
        await updateFund({ ...form.values, id: fund.id });
        setIsEditing(false);
      }
    } catch (error) {
      if (AfternoonDuplicateFieldApiError.isDuplicateFieldError(error)) {
        form.setFieldError(error.field, error.message);
      } else {
        notifyAndLogError(
          'Error',
          'We were not able to update the fund. Please try again or contact support.',
          error,
        );
      }
    }
  };

  const handleCancel = () => {
    if (fund) {
      form.setValues(fund);
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return <CompanyPageLoadingSection />;
  }

  if (!fund || !fundId) {
    return (
      <GenericErrorPage
        logError={false}
        title="Fund not found"
        text="The fund you are looking doesn't seem to exist anymore."
      />
    );
  }

  return (
    <Box>
      <Flex align="center" justify="space-between" mb="xxl" mt="xs" px="xs">
        <FundSearch />
        <DesignButton variant="text" onClick={() => navigate('/company/funds')}>
          Go back
        </DesignButton>
      </Flex>

      <Paper variant="content" p="xxl" mb="xl">
        <Flex justify="space-between" align="center" mb="xxl">
          <Text size="displayXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {fund.name}
          </Text>
          <Flex gap="xs">
            {isEditing ? (
              <>
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={!form.isValid()}>
                  Save
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </Flex>
        </Flex>

        <Box>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4, xxl: 5 }} spacing="xl">
            <Box>
              <Text size="sm" fw={FONT_WEIGHTS.medium} mb="xxs">
                Fund name
              </Text>
              {isEditing ? (
                <TextInput {...form.getInputProps('name')} placeholder="Fund name" />
              ) : (
                <Text size="sm" fw={FONT_WEIGHTS.semibold}>
                  {fund.name}
                </Text>
              )}
            </Box>

            <Box>
              <Text size="sm" fw={FONT_WEIGHTS.medium} mb="xxs">
                Price per unit
              </Text>
              {isEditing ? (
                <NumberInput {...form.getInputProps('pricePerUnit')} leftSection="£" hideControls />
              ) : (
                <Text size="sm" fw={FONT_WEIGHTS.semibold}>
                  {formatCurrency(fund.pricePerUnit, 'GBP', { fractionDigits: 0 })}
                </Text>
              )}
            </Box>

            <Box>
              <Text size="sm" fw={FONT_WEIGHTS.medium} mb="xxs">
                Fund code
              </Text>
              {isEditing ? (
                <TextInput {...form.getInputProps('code')} placeholder="Fund code" />
              ) : (
                <Text size="sm" fw={FONT_WEIGHTS.semibold}>
                  {fund.code}
                </Text>
              )}
            </Box>

            <Box>
              <Text size="sm" fw={FONT_WEIGHTS.medium} mb="xxs">
                Annual management charge
              </Text>
              {isEditing ? (
                <NumberInput
                  {...form.getInputProps('amcPercentage')}
                  rightSection={
                    <Box>
                      <Text>%</Text>
                    </Box>
                  }
                  required
                />
              ) : (
                <Text size="sm" fw={FONT_WEIGHTS.semibold}>
                  {fund.amcPercentage ? formatPercentage(fund.amcPercentage) : 'Unknown'}
                </Text>
              )}
            </Box>
          </SimpleGrid>
        </Box>
      </Paper>

      <FundDocuments fundId={fundId} />
    </Box>
  );
}
